import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { House, ArrowsClockwise, CheckCircle, X, Clock, Coffee, Warning } from '@phosphor-icons/react';
import { fmtIstTime, todayIst } from '../lib/datetime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const today = todayIst;

const STATUS_PILL = {
  present: { bg: '#dcfce7', color: '#15803d', label: 'PRESENT' },
  in_progress: { bg: '#fde68a', color: '#92400e', label: 'WORKING' },
  incomplete: { bg: '#fed7aa', color: '#9a3412', label: 'INCOMPLETE' },
  absent: { bg: '#fee2e2', color: '#b91c1c', label: 'ABSENT' },
  not_checked_in: { bg: '#f1f5f9', color: '#64748b', label: 'NO PUNCH' },
};

const fmtTime = fmtIstTime;

const fmtDur = (min) => {
  const n = Number(min);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0m';
  const h = Math.floor(n / 60), m = n % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};

const CheckInDashboardPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/checkin/admin/day`, { params: { date }, withCredentials: true });
      setData(r.data);
    } catch (e) {
      alert('Load failed: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const markAbsent = async () => {
    if (!window.confirm(`Mark all unmarked employees as ABSENT for ${date}? This also pushes the result to the attendance grid (skips H/WO/CL).`)) return;
    setMarking(true);
    try {
      const r = await axios.post(`${API_URL}/api/checkin/admin/mark-absent`, { date }, { withCredentials: true });
      alert(`Done ✓\n\nPresent: ${r.data.marked_present}\nAbsent: ${r.data.marked_absent}\nIncomplete (forgot to checkout): ${r.data.incomplete}`);
      load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setMarking(false); }
  };

  const approveWfh = async (employee_id) => {
    if (!window.confirm('Approve Work-From-Home for this employee for today?')) return;
    try {
      await axios.post(`${API_URL}/api/checkin/admin/wfh-approve`, { employee_id, date: today() }, { withCredentials: true });
      alert('WFH approved ✓ — they can now check in from any IP today.');
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const decideExcuse = async (ex, decision) => {
    if (!window.confirm(`${decision === 'approved' ? 'Approve' : 'Reject'} the excuse from ${ex.name} for ${ex.date}?`)) return;
    try {
      await axios.post(`${API_URL}/api/checkin/admin/excuse/${ex.id}/decide`, { decision }, { withCredentials: true });
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const counts = useMemo(() => {
    if (!data) return {};
    const c = { present: 0, in_progress: 0, incomplete: 0, absent: 0, not_checked_in: 0 };
    (data.rows || []).forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [data]);

  // Break analytics — total minutes today across staff, who's still on break right now,
  // and who has gone over their allowed break window. Now split by type (Morning / Lunch / Evening).
  const breakStats = useMemo(() => {
    const empty = { count: 0, total: 0, over_count: 0, over_min: 0, breachers: [] };
    if (!data) return { byType: { morning: { ...empty }, lunch: { ...empty }, evening: { ...empty } }, on_break_now: [], over_limit: [] };
    const byType = { morning: { ...empty, breachers: [] }, lunch: { ...empty, breachers: [] }, evening: { ...empty, breachers: [] } };
    const onBreakNow = [];
    const overLimit = [];
    const BUCKET = { tea_morning: 'morning', lunch: 'lunch', tea_evening: 'evening' };
    (data.rows || []).forEach((row) => {
      const r = row.record;
      if (!r) return;
      (r.breaks || []).forEach((b) => {
        const bucket = byType[BUCKET[b.type]];
        if (!bucket) return;
        const min = Number(b.elapsed_minutes || 0);
        const over = Number(b.overtime_minutes || 0);
        bucket.total += min;
        bucket.count += 1;
        if (over > 0) {
          bucket.over_count += 1;
          bucket.over_min += over;
          bucket.breachers.push({ name: row.name, emp_id: row.emp_id, overMin: over, totalMin: min });
        }
        if (b.is_open) onBreakNow.push({ name: row.name, emp_id: row.emp_id, type: BUCKET[b.type] });
      });
      const totalOver = Number(r.break_overtime_minutes || 0);
      if (totalOver > 0) overLimit.push({ name: row.name, emp_id: row.emp_id, overMin: totalOver, breakMin: Number(r.break_minutes || 0) });
    });
    Object.values(byType).forEach((b) => b.breachers.sort((a, b2) => b2.overMin - a.overMin));
    return { byType, on_break_now: onBreakNow, over_limit: overLimit.sort((a, b) => b.overMin - a.overMin) };
  }, [data]);

  return (
    <div data-testid="checkin-dashboard" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>
            <Clock size={22} className="inline mr-1.5 mb-1" /> Daily Check-in Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            Public URL for staff: <code className="px-1.5 py-0.5 bg-gray-100 rounded text-[11px]">{(API_URL || window.location.origin)}/checkin</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-44" data-testid="date-picker" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={markAbsent} disabled={marking} data-testid="mark-absent-btn">
              {marking ? 'Processing…' : 'Mark Absent + Push to Grid'}
            </Button>
          )}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_PILL).map(([key, pill]) => (
          <Card key={key} className="p-3" style={{ borderLeft: `4px solid ${pill.color}` }}>
            <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: pill.color }}>{pill.label}</div>
            <div className="text-2xl font-black mt-0.5" style={{ color: 'var(--dx-text)' }}>{counts[key] || 0}</div>
          </Card>
        ))}
      </div>

      {/* Break analytics — split by type. Each card shows total time, who took it, and red badge if any breach. */}
      {data && (() => {
        const TYPE_META = {
          morning: { label: 'Morning Tea', icon: '☕', accent: '#0ea5e9', limit: 15 },
          lunch:   { label: 'Lunch',       icon: '🍱', accent: '#f59e0b', limit: 45 },
          evening: { label: 'Evening Tea', icon: '🍵', accent: '#8b5cf6', limit: 15 },
        };
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(TYPE_META).map(([key, meta]) => {
              const b = breakStats.byType[key];
              const hasBreach = b.over_count > 0;
              return (
                <Card
                  key={key}
                  className="p-3 relative overflow-hidden transition-all"
                  style={{
                    borderLeft: `4px solid ${hasBreach ? '#dc2626' : meta.accent}`,
                    background: hasBreach ? 'linear-gradient(135deg, #fff1f2 0%, white 60%)' : 'white',
                  }}
                  data-testid={`break-card-${key}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase font-bold tracking-wide flex items-center gap-1" style={{ color: hasBreach ? '#b91c1c' : meta.accent }}>
                        <span>{meta.icon}</span> {meta.label}
                        <span className="text-[9px] font-medium text-gray-400 ml-1">limit {meta.limit}m</span>
                      </div>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <div className="text-2xl font-black tabular-nums" style={{ color: hasBreach ? '#b91c1c' : 'var(--dx-text)' }}>{fmtDur(b.total)}</div>
                        <div className="text-[10px] text-gray-400">·</div>
                        <div className="text-[11px] text-gray-600 tabular-nums">{b.count} taken</div>
                      </div>
                      {hasBreach ? (
                        <div className="text-[10px] text-rose-700 mt-1 font-semibold truncate" title={b.breachers.map((p) => `${p.name} +${p.overMin}m`).join(' · ')}>
                          ⚠ {b.over_count} over · {b.breachers.slice(0, 2).map((p) => `${p.name.split(' ')[0]} +${p.overMin}m`).join(', ')}{b.over_count > 2 ? ` +${b.over_count - 2}` : ''}
                        </div>
                      ) : (
                        <div className="text-[10px] text-emerald-600 mt-1">✓ All within limit</div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
            {/* Composite "Total breaches" card */}
            <Card
              className={`p-3 relative overflow-hidden transition-all ${breakStats.over_limit.length > 0 ? 'ring-2 ring-rose-300' : ''}`}
              style={{
                borderLeft: `4px solid ${breakStats.over_limit.length > 0 ? '#dc2626' : '#94a3b8'}`,
                background: breakStats.over_limit.length > 0 ? 'linear-gradient(135deg, #fff1f2 0%, white 60%)' : 'white',
              }}
              data-testid="over-limit-card"
            >
              <div className="text-[10px] uppercase font-bold tracking-wide flex items-center gap-1" style={{ color: breakStats.over_limit.length > 0 ? '#dc2626' : '#475569' }}>
                <Warning size={11} weight="fill" /> Staff over Limit
              </div>
              <div className="text-2xl font-black mt-0.5 tabular-nums" style={{ color: breakStats.over_limit.length > 0 ? '#b91c1c' : 'var(--dx-text)' }}>
                {breakStats.over_limit.length}
              </div>
              {breakStats.over_limit.length > 0 ? (
                <div className="text-[10px] text-rose-700 mt-0.5 font-semibold leading-relaxed" title={breakStats.over_limit.map((p) => `${p.name}: +${p.overMin}m`).join(' · ')}>
                  {breakStats.over_limit.slice(0, 3).map((p) => `${p.name.split(' ')[0]} +${p.overMin}m`).join(' · ')}{breakStats.over_limit.length > 3 ? ` · +${breakStats.over_limit.length - 3} more` : ''}
                </div>
              ) : (
                <div className="text-[10px] text-emerald-600 mt-0.5">✓ All within limits</div>
              )}
              {breakStats.on_break_now.length > 0 && (
                <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  {breakStats.on_break_now.length} on break now
                </div>
              )}
            </Card>
          </div>
        );
      })()}

      {/* Pending excuses */}
      {data?.excuses?.length > 0 && (
        <Card className="p-0 overflow-hidden border-l-4" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="px-4 py-2.5 text-sm font-semibold" style={{ background: '#fffbeb', color: '#92400e' }}>
            🟡 {data.excuses.filter((e) => e.status === 'pending').length} pending excuse{data.excuses.filter((e) => e.status === 'pending').length !== 1 ? 's' : ''}
          </div>
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--dx-bg)' }}>
              <tr>
                <th className="text-left px-3 py-2 text-xs uppercase font-semibold">Employee</th>
                <th className="text-left px-3 py-2 text-xs uppercase font-semibold">Date</th>
                <th className="text-left px-3 py-2 text-xs uppercase font-semibold">Reason</th>
                <th className="text-left px-3 py-2 text-xs uppercase font-semibold">Status</th>
                <th className="text-right px-3 py-2 text-xs uppercase font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.excuses.map((ex) => (
                <tr key={ex.id} className="border-t" data-testid={`excuse-${ex.id}`}>
                  <td className="px-3 py-2"><strong>{ex.name}</strong> <span className="text-[10px] text-gray-500">{ex.emp_id}</span></td>
                  <td className="px-3 py-2 text-[12px]">{ex.date}</td>
                  <td className="px-3 py-2 text-[12px] text-gray-700">{ex.reason}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                      background: ex.status === 'approved' ? '#dcfce7' : ex.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                      color: ex.status === 'approved' ? '#15803d' : ex.status === 'rejected' ? '#b91c1c' : '#92400e',
                    }}>{ex.status.toUpperCase()}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {ex.status === 'pending' && isAdmin && (
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => decideExcuse(ex, 'approved')} data-testid={`excuse-approve-${ex.id}`}>
                          <CheckCircle size={14} weight="fill" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => decideExcuse(ex, 'rejected')} data-testid={`excuse-reject-${ex.id}`}>
                          <X size={14} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Main grid */}
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--dx-bg)' }}>
            <tr>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">Emp ID</th>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">Name</th>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">Status</th>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">IN</th>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">OUT</th>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">Work</th>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">Breaks</th>
              <th className="text-left px-3 py-2.5 font-semibold text-xs uppercase">OT</th>
              <th className="text-right px-3 py-2.5 font-semibold text-xs uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#52525B]">Loading…</td></tr>
            ) : (data?.rows || []).map((row) => {
              const r = row.record;
              const pill = STATUS_PILL[row.status] || STATUS_PILL.not_checked_in;
              const breaks = (r?.breaks || []);
              return (
                <tr key={row.employee_id} className="border-t hover:bg-gray-50" data-testid={`row-${row.emp_id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{row.emp_id}</td>
                  <td className="px-3 py-2">
                    <button className="font-medium text-left hover:text-blue-600" onClick={() => setDetail(row)}>
                      {row.name}{r?.is_wfh ? <House size={11} className="inline ml-1" style={{ color: '#1e40af' }} /> : ''}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{fmtTime(r?.check_in_at)}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{fmtTime(r?.check_out_at)}</td>
                  <td className="px-3 py-2 text-[11px] font-semibold">{fmtDur(r?.work_minutes)}</td>
                  <td className="px-3 py-2 text-[11px]">
                    {breaks.length > 0 ? (() => {
                      const totalMin = r?.break_minutes || 0;
                      const overMin = r?.break_overtime_minutes || 0;
                      const onBreak = breaks.some((b) => b.is_open);
                      return (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span
                            className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums ${
                              onBreak ? 'bg-amber-100 text-amber-800' :
                              overMin > 0 ? 'bg-rose-100 text-rose-800' :
                              'bg-emerald-50 text-emerald-700'
                            }`}
                            title={`${breaks.length} break(s) · ${totalMin}m total${overMin > 0 ? ` · +${overMin}m over limit` : ''}`}
                            data-testid={`row-breaks-${row.emp_id}`}
                          >
                            <Coffee size={10} weight="fill" />
                            {breaks.length}× · {fmtDur(totalMin)}
                          </span>
                          {onBreak && <span className="text-[9px] font-bold text-amber-700 animate-pulse">● ON</span>}
                          {overMin > 0 && (
                            <span
                              className="text-[10px] font-black px-1 py-0.5 rounded bg-rose-600 text-white"
                              data-testid={`row-break-over-${row.emp_id}`}
                              title={`Exceeded allowed break window by ${overMin} minutes`}
                            >
                              +{overMin}m
                            </span>
                          )}
                        </div>
                      );
                    })() : '—'}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {r?.ot_minutes ? (
                      <span style={{ color: r.ot_approved ? '#15803d' : '#7c2d12' }}>
                        {fmtDur(r.ot_minutes)}{r.ot_approved ? ' ✓' : ' ⏳'}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isAdmin && row.status === 'not_checked_in' && date === today() && (
                      <Button size="sm" variant="outline" onClick={() => approveWfh(row.employee_id)} title="Approve WFH for today" data-testid={`wfh-${row.emp_id}`}>
                        <House size={12} className="mr-1" /> WFH
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <Card className="w-full max-w-2xl bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">{detail.name} <span className="text-xs font-mono text-gray-500">{detail.emp_id}</span></h2>
              <button onClick={() => setDetail(null)} className="text-2xl leading-none px-2">×</button>
            </div>
            {!detail.record ? (
              <p className="text-sm text-gray-600">No check-in record for {date}.</p>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="p-2 rounded bg-green-50"><div className="text-[9px] uppercase font-bold text-green-700">Check IN</div><div className="font-mono font-bold">{fmtTime(detail.record.check_in_at)}</div></div>
                  <div className="p-2 rounded bg-amber-50"><div className="text-[9px] uppercase font-bold text-amber-700">Check OUT</div><div className="font-mono font-bold">{fmtTime(detail.record.check_out_at)}</div></div>
                  <div className="p-2 rounded bg-blue-50"><div className="text-[9px] uppercase font-bold text-blue-700">Work Time</div><div className="font-bold">{fmtDur(detail.record.work_minutes)}</div></div>
                  <div className="p-2 rounded bg-gray-100"><div className="text-[9px] uppercase font-bold text-gray-600">Breaks</div><div className="font-bold">{fmtDur(detail.record.break_minutes)}</div></div>
                  <div className="p-2 rounded bg-purple-50"><div className="text-[9px] uppercase font-bold text-purple-700">OT</div><div className="font-bold">{fmtDur(detail.record.ot_minutes)}{detail.record.ot_approved ? ' ✓' : ' ⏳'}</div></div>
                  <div className="p-2 rounded bg-slate-50"><div className="text-[9px] uppercase font-bold text-slate-600">Location</div><div className="font-bold text-[10px]">{detail.record.is_wfh ? '🏠 WFH' : '🏢 Office'}</div></div>
                </div>

                {/* Breaks detail */}
                {(detail.record.breaks || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase mb-1">Breaks logged</div>
                    <table className="w-full text-xs border">
                      <thead><tr style={{ background: 'var(--dx-bg)' }}><th className="text-left px-2 py-1">Type</th><th className="text-left px-2 py-1">Start</th><th className="text-left px-2 py-1">End</th><th className="text-left px-2 py-1">Duration</th><th className="text-left px-2 py-1">Status</th></tr></thead>
                      <tbody>
                        {detail.record.breaks.map((b, i) => {
                          const elapsed = b.elapsed_minutes ?? (b.end ? Math.max(0, ((new Date(b.end) - new Date(b.start)) / 60000) | 0) : 0);
                          const limit = b.limit_minutes ?? 0;
                          const over = b.overtime_minutes ?? Math.max(0, elapsed - limit);
                          return (
                            <tr key={i} className="border-t">
                              <td className="px-2 py-1 capitalize">{b.type.replace(/_/g, ' ')}</td>
                              <td className="px-2 py-1 font-mono">{b.start || (b.start_iso ? fmtTime(b.start_iso) : '—')}</td>
                              <td className="px-2 py-1 font-mono">{b.end || (b.end_iso ? fmtTime(b.end_iso) : (b.is_open ? 'open' : '—'))}</td>
                              <td className="px-2 py-1 font-mono">{elapsed} min {limit ? <span className="text-[10px] text-gray-400">/ {limit}</span> : null}</td>
                              <td className="px-2 py-1">
                                {b.is_open ? (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">ON BREAK</span>
                                ) : over > 0 ? (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">OVER +{over}m</span>
                                ) : (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">ON TIME</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(detail.record.break_overtime_minutes || 0) > 0 && (
                      <div className="mt-1 text-[11px] font-bold text-rose-700">
                        ⚠ Total break overtime today: +{detail.record.break_overtime_minutes} min
                      </div>
                    )}
                  </div>
                )}

                {/* Selfies */}
                <div className="grid grid-cols-2 gap-3">
                  {detail.record.selfie_in && (
                    <div><div className="text-xs font-bold uppercase mb-1 text-green-700">Selfie IN</div>
                      <img src={detail.record.selfie_in} alt="in" className="w-full rounded border" /></div>
                  )}
                  {detail.record.selfie_out && (
                    <div><div className="text-xs font-bold uppercase mb-1 text-amber-700">Selfie OUT</div>
                      <img src={detail.record.selfie_out} alt="out" className="w-full rounded border" /></div>
                  )}
                </div>

                {/* Meta */}
                <div className="text-[10px] text-gray-500 font-mono space-y-0.5 pt-2 border-t">
                  {detail.record.gps_in && <div>GPS IN: {detail.record.gps_in.lat?.toFixed(4)}, {detail.record.gps_in.lng?.toFixed(4)}</div>}
                  {detail.record.gps_out && <div>GPS OUT: {detail.record.gps_out.lat?.toFixed(4)}, {detail.record.gps_out.lng?.toFixed(4)}</div>}
                  {detail.record.ip_in && <div>IP IN: {detail.record.ip_in}</div>}
                  {detail.record.ip_out && <div>IP OUT: {detail.record.ip_out}</div>}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default CheckInDashboardPage;
